import { Button, Card, Col, notification, Progress, Row, Spin, Tag } from "antd";
import { CalendarOutlined, CrownOutlined, TeamOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { billingCurrentApi } from "../../config/Api";
import { apiErrorMessage } from "../../services/ApiError";
import { setHeaderName } from "../../services/SetHeaderName";
import { PendingPayments } from "./PendingPayments";
import { PlanHistory } from "./PlanHistory";
import { PlanOptions } from "./PlanOptions";
import { useFreePlan } from "./useFreePlan";
import "./style/MyPlan.scss";

const showDate = (date) => (date ? moment(date).format('DD MMM YYYY') : '')
const cycleLabel = (cycle) => (cycle === 'yearly' ? 'Yearly' : cycle === 'monthly' ? 'Monthly' : 'Free')

// the doctor's plan: what they have, how much is used, renew or change it, and their history
export const MyPlan = ({ embedded }) => {
    const [current, setCurrent] = useState(null)
    // bumped after a payment so the history reloads
    const [version, setVersion] = useState(0)

    const load = () => {
        billingCurrentApi()
            .then((res) => {
                setCurrent(res.data.data)
                setVersion((v) => v + 1)
            })
            .catch((err) => notification.error({ message: apiErrorMessage(err) }))
    }

    useEffect(() => {
        if (!embedded) setHeaderName('My Plan')
        load()
    }, [])

    const { chooseFree, saving, modal } = useFreePlan(load)

    if (!current) return <div className="text-center p-5"><Spin /></div>

    const plan = current.plan
    const limit = plan?.active_clients || 0
    const used = current.active_clients_used
    const usedPercent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0
    const endsSoon = current.days_left !== null && current.days_left <= 7

    // how much of the paid period has gone by
    const periodDays = current.starts_at && current.ends_at ? Math.max(1, moment(current.ends_at).diff(moment(current.starts_at), 'days')) : null
    const periodPercent = periodDays ? Math.min(100, Math.max(0, Math.round(((periodDays - current.days_left) / periodDays) * 100))) : null

    return (
        <div className="MyPlan">
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} lg={15}>
                    <div className="my-plan-hero">
                        <div className="my-plan-hero-label"><CrownOutlined className="me-2" />Current plan</div>
                        <div className="my-plan-hero-name">
                            {plan?.name || 'No plan'}
                            {plan && <Tag className="ms-2 my-plan-hero-tag">{cycleLabel(current.billing_cycle)}</Tag>}
                        </div>

                        {plan?.is_free ? (
                            <div className="my-plan-hero-sub">Never expires. Upgrade any time for more active clients.</div>
                        ) : current.ends_at ? (
                            <>
                                <div className="my-plan-hero-sub">
                                    <CalendarOutlined className="me-2" />
                                    Valid until <b>{showDate(current.ends_at)}</b>
                                    <span className={endsSoon ? 'my-plan-hero-warn' : ''}> · {current.days_left} day{current.days_left === 1 ? '' : 's'} left</span>
                                </div>
                                {periodPercent !== null && (
                                    <Progress
                                        className="my-plan-hero-progress"
                                        percent={periodPercent}
                                        showInfo={false}
                                        strokeColor={endsSoon ? '#ffb4a8' : '#ffffff'}
                                        trailColor="rgba(255,255,255,0.25)"
                                    />
                                )}
                                <div className="my-plan-hero-note">
                                    {endsSoon
                                        ? 'Renew now: the new period starts when this one ends, so no days are lost.'
                                        : `Renewal opens 7 days before the end date. You can upgrade any time.`}
                                </div>
                            </>
                        ) : null}
                    </div>
                </Col>
                <Col xs={24} lg={9}>
                    <Card className="my-plan-usage h-100">
                        <div className="d-flex align-items-center gap-4">
                            <Progress
                                type="dashboard"
                                percent={usedPercent}
                                width={110}
                                strokeColor={usedPercent >= 100 ? '#cf1322' : usedPercent >= 80 ? '#fa8c16' : '#5F4B8B'}
                                format={() => <span style={{ fontSize: 20 }}>{used}<span style={{ fontSize: 13, color: '#888' }}>/{limit.toLocaleString('en-IN')}</span></span>}
                            />
                            <div>
                                <div className="my-plan-usage-title"><TeamOutlined className="me-2" />Active clients</div>
                                <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                                    {used >= limit ? 'Your plan is full.' : `${(limit - used).toLocaleString('en-IN')} more can be active on this plan.`}
                                </div>
                                <Link to="/client-list"><Button size="small">Manage clients</Button></Link>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            <PendingPayments payments={current.pending_payments} onPaid={load} />

            <Card
                className="mb-4"
                title="Renew or change plan"
                extra={<span className="text-muted" style={{ fontSize: 13 }}>Upgrades start today and credit your unused days</span>}
            >
                <PlanOptions
                    current={current}
                    onFree={current.can_choose_free ? () => chooseFree() : undefined}
                    freeLoading={saving}
                    onPaid={load}
                    onPending={load}
                />
            </Card>

            <Card title="Plan history">
                <PlanHistory reload={version} />
            </Card>
            {modal}
        </div>
    )
}
